import { VARIETIES_DATA_INDEX } from 'constants/gcConstants';
import type { GcVariety } from 'interfaces/IGlobalCatalogue';
import type {
  MExtraVarietyData,
  MSeedSpecies,
  MSeedSpeciesExtra,
} from 'interfaces/IMasterDb';

const VARIETY_ID_FIELD = 'varietyId';

export interface NewVarietyValues {
  name: string;
  supplier?: string | null;
  techStack?: { id: string; name: string } | null;
}

export const isNewVarietyValid = (values: NewVarietyValues): boolean =>
  !!values.name.trim();

const trimmedOrNull = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

/** The variety as stored on a crop, with only the details the user filled in. */
export const buildNewVariety = (
  varietyId: string,
  values: NewVarietyValues
): MExtraVarietyData => {
  const variety: MExtraVarietyData = {
    varietyId,
    varietyName: values.name.trim(),
  };
  const supplier = trimmedOrNull(values.supplier);
  if (supplier) {
    variety.snapSupplier = supplier;
    variety.supplierId = null;
  }
  if (values.techStack) {
    variety.techStackId = values.techStack.id;
    variety.snapTechStack = values.techStack.name;
  }
  return variety;
};

/** The same variety in the picker's shape, so it can be selected straight away. */
export const toGcVariety = (variety: MExtraVarietyData): GcVariety => ({
  varietyId: variety.varietyId,
  varietyName: variety.varietyName,
  techStackId: variety.techStackId ?? null,
  snapTechStack: variety.snapTechStack ?? null,
  supplierId: variety.supplierId ?? null,
  snapSupplier: variety.snapSupplier ?? null,
});

export interface VarietyExtras {
  /** The crop's whole extras list: Update replaces it, it does not append. */
  extras: MSeedSpeciesExtra[];
  /** True when the crop held varieties on its root, which are moved into extras. */
  clearRootVarieties: boolean;
}

/**
 * The extras to send for a crop gaining a variety. With no M document yet
 * (a global crop) it is just the new variety. Otherwise it is every variety
 * already held in extras, plus any held on the root (moved, as mobile does,
 * because a root list would replace the global crop's varieties), plus the new one.
 */
export const buildVarietyExtras = (
  existing: MSeedSpecies | undefined,
  newVariety: MExtraVarietyData
): VarietyExtras => {
  const otherExtras = (existing?.extras ?? []).filter(
    (extra) => extra.attrArrayName !== VARIETIES_DATA_INDEX
  );
  const currentVarieties =
    (existing?.extras ?? []).find(
      (extra) => extra.attrArrayName === VARIETIES_DATA_INDEX
    )?.extraData ?? [];
  const rootVarieties = existing?.varieties ?? [];

  const byId = new Map<string, MExtraVarietyData>();
  [...currentVarieties, ...rootVarieties, newVariety].forEach((variety) =>
    byId.set(variety.varietyId, variety)
  );

  return {
    extras: [
      ...otherExtras,
      {
        attrArrayName: VARIETIES_DATA_INDEX,
        attrIdName: VARIETY_ID_FIELD,
        extraData: Array.from(byId.values()),
      },
    ],
    clearRootVarieties: rootVarieties.length > 0,
  };
};
